import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import QRCode from "qrcode";
import PublicNav from "../../components/layout/PublicNav";
import PublicFooter from "../../components/layout/PublicFooter";
import PageMeta from "../../components/seo/PageMeta";
import {
  HeartHandshake, Stethoscope, Microscope, Download, Printer, FileText,
  ArrowRight, ArrowUpRight, CheckCircle2, Clock, ShieldCheck, Sparkles,
  RefreshCcw, FileJson, Share2, Mail, Link2, Copy, X, AlertCircle, QrCode, ScanLine,
} from "lucide-react";
import { encodeShare, SHARE_LINK_MAX_SAFE_BYTES, QR_FIT_MAX_BYTES } from "../../utils/shareLink";

/* ------------------------------------------------------------------ */
/*  Config — the 3 checklists that feed into "My Checklists"           */
/* ------------------------------------------------------------------ */

const CHECKLISTS = [
  {
    id: "nurse",
    label: "Nurse",
    fullLabel: "Nurse Skills Checklist",
    storageKey: "tc_nurse_checklist_v1",
    submissionKey: "tc_nurse_checklist_v1:submission",
    builderPath: "/compliance/checklists/nurse",
    Icon: HeartHandshake,
    accent: "bg-trital-medical-blue-soft text-trital-navy",
    accentActive: "bg-trital-navy text-white",
  },
  {
    id: "therapist",
    label: "Therapist",
    fullLabel: "Therapist Skills Checklist",
    storageKey: "tc_therapist_checklist_v1",
    submissionKey: "tc_therapist_checklist_v1:submission",
    builderPath: "/compliance/checklists/therapist",
    Icon: Stethoscope,
    accent: "bg-red-50 text-trital-red",
    accentActive: "bg-trital-red text-white",
  },
  {
    id: "other",
    label: "Allied",
    fullLabel: "Allied Health Checklist",
    storageKey: "tc_other_checklist_v1",
    submissionKey: "tc_other_checklist_v1:submission",
    builderPath: "/compliance/checklists/other",
    Icon: Microscope,
    accent: "bg-trital-medical-blue-soft text-trital-navy",
    accentActive: "bg-trital-navy text-white",
  },
];

/* ------------------------------------------------------------------ */
/*  Helpers                                                             */
/* ------------------------------------------------------------------ */

function readJSON(key) {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function computeLocal(draft, submission) {
  /* Prefer submission stats if present; otherwise derive from the draft */
  const source = submission || (draft ? { entries: draft, summary: null } : null);
  if (!source) {
    return {
      hasDraft: false,
      hasSubmission: false,
      specialtiesStarted: 0,
      specialtiesAttested: 0,
      ratedItems: 0,
      lastTouchedAt: null,
    };
  }
  const entries = source.entries || draft || {};
  const started = Object.keys(entries).filter((k) => {
    const e = entries[k];
    return !!e && (e.yearsOfExperience !== "" || e.overallConfidence > 0 || e.notes?.length || Object.keys(e.items || {}).length > 0);
  });
  const attested = started.filter((k) => entries[k]?.attested);
  const ratedItems = Object.values(entries).reduce((n, e) => n + Object.keys(e?.items || {}).length, 0);
  return {
    hasDraft: !!draft,
    hasSubmission: !!submission,
    specialtiesStarted: started.length,
    specialtiesAttested: attested.length,
    ratedItems,
    lastTouchedAt: submission?.submittedAt || null,
  };
}

function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

/* ------------------------------------------------------------------ */
/*  Page                                                                */
/* ------------------------------------------------------------------ */

export default function MyChecklists() {
  const [records, setRecords] = useState([]);
  const [shareState, setShareState] = useState(null); // { url, expiresAt, sizeBytes, ttlLabel }
  const [ttlDays, setTtlDays] = useState(14);

  const refresh = () => {
    setRecords(
      CHECKLISTS.map((c) => {
        const draft = readJSON(c.storageKey);
        const submission = readJSON(c.submissionKey);
        return { ...c, draft, submission, local: computeLocal(draft, submission) };
      })
    );
  };

  useEffect(() => {
    refresh();
    /* Sync across tabs */
    const onStorage = (e) => {
      if (!e.key) return refresh();
      if (CHECKLISTS.some((c) => e.key === c.storageKey || e.key === c.submissionKey)) refresh();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const totals = records.reduce(
    (acc, r) => ({
      started: acc.started + r.local.specialtiesStarted,
      attested: acc.attested + r.local.specialtiesAttested,
      rated: acc.rated + r.local.ratedItems,
      submissions: acc.submissions + (r.local.hasSubmission ? 1 : 0),
      drafts: acc.drafts + (r.local.hasDraft ? 1 : 0),
    }),
    { started: 0, attested: 0, rated: 0, submissions: 0, drafts: 0 }
  );

  const hasAnything = records.some((r) => r.local.hasDraft || r.local.hasSubmission);
  const hasSubmissions = totals.submissions > 0;

  const buildAggregate = () => ({
    generatedAt: new Date().toISOString(),
    clinicianSummary: {
      totalsAcrossAllChecklists: totals,
    },
    checklists: records.map((r) => ({
      id: r.id,
      label: r.fullLabel,
      status: r.local.hasSubmission ? "submitted" : r.local.hasDraft ? "draft" : "not_started",
      submittedAt: r.submission?.submittedAt || null,
      summary: r.submission?.summary || null,
      draft: r.submission ? null : r.draft || null,
      entries: r.submission?.entries || r.draft || null,
    })),
  });

  const handleDownloadAll = () => {
    if (!hasAnything) {
      toast.error("No checklists to download yet.");
      return;
    }
    const blob = new Blob([JSON.stringify(buildAggregate(), null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tritalcare-my-checklists-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Downloaded — share with your recruiter.");
  };

  const handleCopyLink = async () => {
    try {
      const url = window.location.href;
      await navigator.clipboard.writeText(url);
      toast.success("Page link copied.");
    } catch {
      toast.error("Couldn't copy link.");
    }
  };

  const handleEmailAggregate = () => {
    const subject = encodeURIComponent("My TRITAL Care® skills checklists");
    const body = encodeURIComponent(
      `Hi,\n\nAttaching my TRITAL Care® skills checklists (${totals.attested} attested specialties across ${records.filter((r) => r.local.hasSubmission).length} disciplines).\n\nPlease find the JSON export attached.\n\n— Sent from tritalcare.com`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleGenerateShare = (overrideTtlDays) => {
    if (!hasAnything) {
      toast.error("Complete a checklist before creating a shareable link.");
      return;
    }
    const payload = buildAggregate();
    const days = typeof overrideTtlDays === "number" ? overrideTtlDays : ttlDays;
    const ttlMs = days * 24 * 60 * 60 * 1000;
    const result = encodeShare(payload, { ttlMs });
    setShareState({ ...result, ttlDays: days });
    if (result.sizeBytes > SHARE_LINK_MAX_SAFE_BYTES) {
      toast.warning("Link is large — some email clients may truncate it.", {
        description: `${(result.sizeBytes / 1024).toFixed(1)} KB. Prefer Download-all JSON for very detailed records.`,
      });
    }
  };

  const handleCopyShareUrl = async () => {
    if (!shareState) return;
    try {
      await navigator.clipboard.writeText(shareState.url);
      toast.success("Shareable link copied.", { description: "Send it to a recruiter — it decodes in the browser." });
    } catch {
      toast.error("Couldn't copy — try the manual selection.");
    }
  };

  const handleEmailShare = () => {
    if (!shareState) return;
    const subject = encodeURIComponent("My TRITAL Care® skills checklist");
    const body = encodeURIComponent(
      `Hi,\n\nHere's a shareable link to my TRITAL Care® skills checklist (expires ${new Date(shareState.expiresAt).toLocaleDateString()}):\n\n${shareState.url}\n\n— Sent from tritalcare.com`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handlePrint = () => window.print();

  const handleReset = () => {
    if (!window.confirm("Clear ALL checklist data (drafts + submissions) on this device?")) return;
    CHECKLISTS.forEach((c) => {
      window.localStorage.removeItem(c.storageKey);
      window.localStorage.removeItem(c.submissionKey);
    });
    refresh();
    toast("Cleared all checklist data.", { description: "You can start fresh anytime." });
  };

  return (
    <div className="min-h-screen bg-white" data-testid="my-checklists">
      <PageMeta
        title="My Checklists | TRITAL Care®"
        description="Unified view of your Nurse, Therapist, and Allied-Health skill checklists. Download a single recruiter-ready JSON with all ratings, attestations, and submissions."
        canonical="https://www.tritalcare.com/my-checklists"
      />
      <PublicNav />

      {/* HERO ------------------------------------------------------ */}
      <section className="relative overflow-hidden bg-trital-mesh" data-testid="my-checklists-hero">
        <div className="absolute inset-0 dot-grid opacity-60 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-14 pb-14 lg:pt-20 lg:pb-20">
          <nav aria-label="Breadcrumb" className="text-xs text-slate-500 mb-6">
            <Link to="/" className="hover:text-trital-navy">Home</Link>
            <span className="mx-2 text-slate-300">/</span>
            <Link to="/compliance/checklists" className="hover:text-trital-navy">Checklists</Link>
            <span className="mx-2 text-slate-300">/</span>
            <span className="text-slate-700">My Checklists</span>
          </nav>

          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-card">
                <FileText className="w-3.5 h-3.5 text-trital-navy" strokeWidth={1.8} />
                <span className="overline text-trital-navy">My Checklists</span>
              </div>
              <h1 className="mt-6 font-display font-black text-4xl lg:text-[56px] leading-[1.04] tracking-tight text-slate-900 text-balance">
                One profile. <span className="text-trital-navy">Every competency you've rated.</span>
              </h1>
              <p className="mt-5 text-slate-600 max-w-xl leading-relaxed">
                A unified view of your Nurse, Therapist, and Allied-Health skill checklists. Built for multi-credential clinicians — RNs who moonlight as RTs, PTs with pediatric specialties, sonographers with vascular credentials. One download, one share, one recruiter-ready record.
              </p>

              <div className="mt-7 flex flex-wrap gap-2">
                <button
                  onClick={handleGenerateShare}
                  disabled={!hasAnything}
                  className="inline-flex items-center gap-1.5 px-5 py-3 rounded-full bg-trital-red text-white text-[13.5px] font-semibold btn-lift hover:bg-trital-red-deep disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="my-btn-share"
                >
                  <Link2 className="w-3.5 h-3.5" strokeWidth={1.8} /> Create shareable link
                </button>
                <button
                  onClick={handleDownloadAll}
                  disabled={!hasAnything}
                  className="inline-flex items-center gap-1.5 px-5 py-3 rounded-full bg-trital-navy text-white text-[13.5px] font-semibold btn-lift hover:bg-trital-navy-deep disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="my-btn-download-all"
                >
                  <Download className="w-3.5 h-3.5" strokeWidth={1.8} /> Download all (JSON)
                </button>
                <button
                  onClick={handlePrint}
                  disabled={!hasAnything}
                  className="inline-flex items-center gap-1.5 px-4 py-3 rounded-full bg-white border border-slate-300 text-[13.5px] font-semibold text-trital-navy hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="my-btn-print"
                >
                  <Printer className="w-3.5 h-3.5" strokeWidth={1.8} /> Print summary
                </button>
                <button
                  onClick={handleEmailAggregate}
                  disabled={!hasAnything}
                  className="inline-flex items-center gap-1.5 px-4 py-3 rounded-full bg-white border border-slate-300 text-[13.5px] font-semibold text-trital-navy hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="my-btn-email"
                >
                  <Mail className="w-3.5 h-3.5" strokeWidth={1.8} /> Email recruiter
                </button>
                <button
                  onClick={handleCopyLink}
                  className="inline-flex items-center gap-1.5 px-4 py-3 rounded-full bg-white border border-slate-300 text-[13.5px] font-semibold text-trital-navy hover:bg-slate-50"
                  data-testid="my-btn-copy-link"
                >
                  <Share2 className="w-3.5 h-3.5" strokeWidth={1.8} /> Copy page link
                </button>
              </div>
            </div>

            {/* Aggregate totals */}
            <div className="lg:col-span-5">
              <div className="rounded-3xl border border-slate-200 bg-white shadow-navy p-6 lg:p-7 relative overflow-hidden" data-testid="my-totals-card">
                <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-trital-medical-blue-soft blur-3xl" />
                <div className="relative">
                  <div className="overline text-trital-red">At a glance</div>
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <div className="rounded-xl border border-slate-200 p-3">
                      <div className="text-[11px] text-slate-500">Specialties attested</div>
                      <div className="mt-1 font-display font-black text-3xl text-trital-navy">{totals.attested}</div>
                    </div>
                    <div className="rounded-xl border border-slate-200 p-3">
                      <div className="text-[11px] text-slate-500">Competencies rated</div>
                      <div className="mt-1 font-display font-black text-3xl text-trital-navy">{totals.rated}</div>
                    </div>
                    <div className="rounded-xl border border-slate-200 p-3">
                      <div className="text-[11px] text-slate-500">Submissions</div>
                      <div className="mt-1 font-display font-black text-3xl text-emerald-600">
                        {totals.submissions}
                      </div>
                    </div>
                    <div className="rounded-xl border border-slate-200 p-3">
                      <div className="text-[11px] text-slate-500">Drafts in progress</div>
                      <div className="mt-1 font-display font-black text-3xl text-amber-500">
                        {totals.drafts - totals.submissions > 0 ? totals.drafts - totals.submissions : 0}
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 flex items-center gap-2 text-[11.5px] text-slate-500">
                    <ShieldCheck className="w-3.5 h-3.5 text-trital-navy" strokeWidth={1.8} />
                    Stored locally on this device · Private by default
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CHECKLIST CARDS ------------------------------------------- */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-16" data-testid="my-checklist-cards">
        <div className="grid md:grid-cols-3 gap-5">
          {records.map((r, i) => {
            const { Icon } = r;
            const status = r.local.hasSubmission ? "submitted" : r.local.hasDraft ? "draft" : "empty";
            return (
              <motion.div
                key={r.id}
                initial={{ opacity: 1, y: 0 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.35, delay: i * 0.05 }}
                className="rounded-3xl bg-white border border-slate-200 p-6 lg:p-7 shadow-card relative overflow-hidden"
                data-testid={`my-card-${r.id}`}
              >
                <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-trital-medical-blue-soft blur-2xl opacity-40" />
                <div className="relative">
                  <div className="flex items-center justify-between">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${r.local.hasSubmission ? r.accentActive : r.accent}`}>
                      <Icon className="w-6 h-6" strokeWidth={1.8} />
                    </div>
                    <StatusPill status={status} />
                  </div>

                  <h3 className="mt-5 font-display font-bold text-[20px] text-slate-900">
                    {r.fullLabel}
                  </h3>

                  <dl className="mt-4 space-y-2 text-[13px]">
                    <div className="flex items-center justify-between">
                      <dt className="text-slate-500">Specialties attested</dt>
                      <dd className="font-display font-bold text-slate-900">
                        {r.local.specialtiesAttested}
                      </dd>
                    </div>
                    <div className="flex items-center justify-between">
                      <dt className="text-slate-500">Competencies rated</dt>
                      <dd className="font-display font-bold text-slate-900">
                        {r.local.ratedItems}
                      </dd>
                    </div>
                    {r.local.lastTouchedAt && (
                      <div className="flex items-center justify-between">
                        <dt className="text-slate-500 inline-flex items-center gap-1">
                          <Clock className="w-3 h-3" strokeWidth={1.8} /> Submitted
                        </dt>
                        <dd className="font-medium text-slate-700 text-[12px]">
                          {fmtDate(r.local.lastTouchedAt)}
                        </dd>
                      </div>
                    )}
                  </dl>

                  <div className="mt-6 pt-5 border-t border-slate-100 flex flex-wrap gap-2">
                    <Link
                      to={r.builderPath}
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-trital-navy text-white text-[12.5px] font-semibold btn-lift hover:bg-trital-navy-deep"
                      data-testid={`my-card-${r.id}-open`}
                    >
                      {r.local.hasSubmission ? "Update" : r.local.hasDraft ? "Resume" : "Start"} <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.8} />
                    </Link>
                    {(r.local.hasDraft || r.local.hasSubmission) && (
                      <button
                        onClick={() => {
                          const payload = r.submission || { draftAt: new Date().toISOString(), entries: r.draft };
                          const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = `tritalcare-${r.id}-checklist-${new Date().toISOString().slice(0, 10)}.json`;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          URL.revokeObjectURL(url);
                        }}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-white border border-slate-300 text-[12.5px] font-semibold text-trital-navy hover:bg-slate-50"
                        data-testid={`my-card-${r.id}-download`}
                      >
                        <FileJson className="w-3.5 h-3.5" strokeWidth={1.8} /> JSON
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* EMPTY / GETTING-STARTED ---------------------------------- */}
      {!hasAnything && (
        <section className="max-w-4xl mx-auto px-5 lg:px-8 pb-20" data-testid="my-empty-state">
          <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-8 lg:p-10 text-center">
            <div className="w-14 h-14 rounded-2xl bg-white border border-slate-200 mx-auto flex items-center justify-center">
              <FileText className="w-6 h-6 text-trital-navy" strokeWidth={1.8} />
            </div>
            <h3 className="mt-5 font-display font-black text-2xl text-slate-900">Nothing here yet.</h3>
            <p className="mt-3 text-[14.5px] text-slate-600 max-w-lg mx-auto leading-relaxed">
              Start with any of the three clinical skill checklists. Your progress saves to this device automatically, and shows up here the moment you return.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              {CHECKLISTS.map((c) => (
                <Link
                  key={c.id}
                  to={c.builderPath}
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white border border-slate-300 text-[13px] font-semibold text-trital-navy hover:bg-slate-100"
                  data-testid={`my-empty-start-${c.id}`}
                >
                  <c.Icon className="w-3.5 h-3.5" strokeWidth={1.8} /> Start {c.label}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* RECRUITER-READY EXPORT DESCRIPTION ----------------------- */}
      {hasAnything && (
        <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-16">
          <div className="rounded-3xl bg-trital-navy-ink text-white relative overflow-hidden" data-testid="my-export-band">
            <div className="absolute inset-0 bg-trital-navy-mesh opacity-85 pointer-events-none" />
            <div className="absolute inset-0 dot-grid opacity-10" aria-hidden="true" />
            <div className="relative p-8 lg:p-10 grid lg:grid-cols-[1.4fr_1fr] gap-8 items-center">
              <div>
                <div className="overline text-trital-red flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3" strokeWidth={1.8} /> Recruiter-ready format
                </div>
                <h3 className="mt-3 font-display font-black text-3xl lg:text-4xl leading-[1.1] text-balance">
                  One JSON. Every discipline you've rated.
                </h3>
                <p className="mt-3 text-white/80 max-w-[56ch] leading-relaxed text-[14.5px]">
                  The aggregate file includes per-discipline entries, per-item ratings, years of experience, notes, attestation timestamps, and submission metadata. Drop it into any ATS, VMS, or compliance system — no re-keying.
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/10 text-[11px] font-bold uppercase tracking-wider">
                    <CheckCircle2 className="w-3 h-3" strokeWidth={2} /> ATS-friendly JSON
                  </span>
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/10 text-[11px] font-bold uppercase tracking-wider">
                    <ShieldCheck className="w-3 h-3" strokeWidth={2} /> No PHI sent
                  </span>
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/10 text-[11px] font-bold uppercase tracking-wider">
                    <Clock className="w-3 h-3" strokeWidth={2} /> Timestamped
                  </span>
                </div>
              </div>
              <div className="flex flex-col gap-3">
                <button
                  onClick={handleGenerateShare}
                  className="inline-flex items-center justify-center gap-1.5 px-5 py-3.5 rounded-full bg-white text-slate-900 text-[13.5px] font-semibold btn-lift hover:bg-slate-100"
                  data-testid="my-export-band-share"
                >
                  <Link2 className="w-4 h-4" strokeWidth={1.8} /> Create shareable link
                </button>
                <button
                  onClick={handleDownloadAll}
                  className="inline-flex items-center justify-center gap-1.5 px-5 py-3.5 rounded-full bg-trital-red text-white text-[13.5px] font-semibold btn-lift hover:bg-trital-red-deep"
                  data-testid="my-export-band-download"
                >
                  <Download className="w-4 h-4" strokeWidth={1.8} /> Download all (JSON)
                </button>
                <button
                  onClick={handleEmailAggregate}
                  className="inline-flex items-center justify-center gap-1.5 px-5 py-3.5 rounded-full bg-white/10 text-white border border-white/20 text-[13.5px] font-semibold hover:bg-white/20"
                  data-testid="my-export-band-email"
                >
                  <Mail className="w-4 h-4" strokeWidth={1.8} /> Email to recruiter
                </button>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-1.5 px-5 py-3.5 rounded-full border border-white/25 text-white text-[13.5px] font-semibold hover:bg-white/10"
                  data-testid="my-export-band-talk"
                >
                  Talk to a recruiter <ArrowUpRight className="w-4 h-4" strokeWidth={1.8} />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* FOOTER TOOLS --------------------------------------------- */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-20">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 pt-6 border-t border-slate-200">
          <div className="text-[12.5px] text-slate-500">
            {hasSubmissions
              ? `${totals.submissions} submission${totals.submissions === 1 ? "" : "s"} · ${totals.attested} attested specialties`
              : "No submissions yet — complete any checklist and attest to appear here."}
          </div>
          <div className="flex gap-2">
            <Link
              to="/compliance/checklists"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-slate-300 text-[12.5px] font-semibold text-trital-navy hover:bg-slate-50"
              data-testid="my-footer-hub"
            >
              Checklist hub
            </Link>
            <button
              onClick={refresh}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-slate-300 text-[12.5px] font-semibold text-trital-navy hover:bg-slate-50"
              data-testid="my-footer-refresh"
            >
              <RefreshCcw className="w-3.5 h-3.5" strokeWidth={1.8} /> Refresh
            </button>
            {hasAnything && (
              <button
                onClick={handleReset}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-slate-300 text-[12.5px] font-semibold text-slate-500 hover:text-trital-red hover:border-trital-red"
                data-testid="my-footer-reset-all"
              >
                <RefreshCcw className="w-3.5 h-3.5" strokeWidth={1.8} /> Clear all
              </button>
            )}
          </div>
        </div>
      </section>

      <ShareLinkModal
        shareState={shareState}
        ttlDays={ttlDays}
        setTtlDays={setTtlDays}
        onClose={() => setShareState(null)}
        onCopy={handleCopyShareUrl}
        onRegenerate={handleGenerateShare}
        onEmail={handleEmailShare}
      />

      <PublicFooter />
    </div>
  );
}

function StatusPill({ status }) {
  if (status === "submitted") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[10.5px] font-bold uppercase tracking-wider">
        <CheckCircle2 className="w-3 h-3" strokeWidth={2} /> Submitted
      </span>
    );
  }
  if (status === "draft") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-50 text-amber-700 text-[10.5px] font-bold uppercase tracking-wider">
        <Clock className="w-3 h-3" strokeWidth={2} /> In progress
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 text-slate-500 text-[10.5px] font-bold uppercase tracking-wider">
      Not started
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Share-link modal                                                   */
/* ------------------------------------------------------------------ */

function ShareLinkModal({ shareState, ttlDays, setTtlDays, onClose, onCopy, onRegenerate, onEmail }) {
  const [tab, setTab] = useState("link"); // 'link' | 'qr'
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [qrError, setQrError] = useState(null);

  const tooLarge = shareState && shareState.sizeBytes > SHARE_LINK_MAX_SAFE_BYTES;
  const sizeKB = shareState ? (shareState.sizeBytes / 1024).toFixed(1) : "0.0";
  const expiresDate = shareState ? new Date(shareState.expiresAt) : null;

  /* QR code capacity limits out around ~2953 chars. Anything larger won't fit. */
  const qrFits = shareState && shareState.sizeBytes <= QR_FIT_MAX_BYTES;

  useEffect(() => {
    if (!shareState || tab !== "qr" || !qrFits) {
      setQrDataUrl(null);
      setQrError(null);
      return;
    }
    let cancelled = false;
    QRCode.toDataURL(shareState.url, {
      errorCorrectionLevel: "M",
      margin: 1,
      width: 420,
      color: { dark: "#0A1F3F", light: "#FFFFFF" },
    })
      .then((d) => { if (!cancelled) { setQrDataUrl(d); setQrError(null); } })
      .catch((e) => { if (!cancelled) { setQrDataUrl(null); setQrError(String(e?.message || e)); } });
    return () => { cancelled = true; };
  }, [shareState, tab, qrFits]);

  const handleDownloadQr = () => {
    if (!qrDataUrl) return;
    const a = document.createElement("a");
    a.href = qrDataUrl;
    a.download = `tritalcare-checklist-qr-${new Date().toISOString().slice(0, 10)}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <AnimatePresence>
      {shareState && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm print:hidden"
          data-testid="my-share-modal"
          onClick={(e) => e.target === e.currentTarget && onClose()}
        >
          <motion.div
            initial={{ scale: 0.96, y: 10, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.96, y: 10, opacity: 0 }}
            transition={{ duration: 0.22 }}
            className="relative w-full max-w-lg rounded-3xl bg-white shadow-navy p-7 lg:p-8 border border-slate-200 overflow-hidden"
          >
            <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-trital-medical-blue-soft blur-3xl" />
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100 text-slate-500"
              aria-label="Close"
              data-testid="my-share-close"
            >
              <X className="w-4 h-4" strokeWidth={1.8} />
            </button>

            <div className="relative">
              <div className="w-14 h-14 rounded-2xl bg-trital-medical-blue-soft text-trital-navy flex items-center justify-center">
                <Link2 className="w-7 h-7" strokeWidth={1.8} />
              </div>
              <div className="overline text-trital-red mt-5 flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" strokeWidth={1.8} /> Shareable link ready
              </div>
              <h3 className="mt-2 font-display font-black text-[26px] leading-[1.1] text-slate-900 text-balance">
                One link. Zero attachments.
              </h3>
              <p className="mt-3 text-[13.5px] text-slate-600 leading-relaxed">
                The link decodes in the recipient's browser — nothing is stored on TRITAL Care® servers.
                Perfect for recruiter emails, LinkedIn messages, VMS replies — or scan-to-pull at a career fair.
              </p>

              {/* Tabs: Link | QR */}
              <div className="mt-6 inline-flex rounded-full border border-slate-200 bg-slate-50 p-1" role="tablist" data-testid="my-share-tabs">
                <button
                  role="tab"
                  aria-selected={tab === "link"}
                  onClick={() => setTab("link")}
                  className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[12.5px] font-semibold transition-all ${
                    tab === "link" ? "bg-white text-trital-navy shadow-sm" : "text-slate-600 hover:text-slate-900"
                  }`}
                  data-testid="my-share-tab-link"
                >
                  <Link2 className="w-3.5 h-3.5" strokeWidth={1.8} /> Link
                </button>
                <button
                  role="tab"
                  aria-selected={tab === "qr"}
                  onClick={() => setTab("qr")}
                  className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[12.5px] font-semibold transition-all ${
                    tab === "qr" ? "bg-white text-trital-navy shadow-sm" : "text-slate-600 hover:text-slate-900"
                  }`}
                  data-testid="my-share-tab-qr"
                >
                  <QrCode className="w-3.5 h-3.5" strokeWidth={1.8} /> QR code
                </button>
              </div>

              {/* TTL selector */}
              <div className="mt-5">
                <div className="text-[11.5px] font-bold tracking-[0.14em] uppercase text-trital-navy mb-2">
                  Link expires after
                </div>
                <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Link time-to-live">
                  {[1, 7, 14, 30].map((d) => (
                    <button
                      key={d}
                      onClick={() => {
                        setTtlDays(d);
                        onRegenerate(d);
                      }}
                      aria-pressed={ttlDays === d}
                      className={`px-3.5 py-2 rounded-full border text-[12.5px] font-semibold transition-all ${
                        ttlDays === d
                          ? "bg-trital-navy text-white border-trital-navy"
                          : "bg-white border-slate-300 text-slate-700 hover:border-trital-navy/40"
                      }`}
                      data-testid={`my-share-ttl-${d}`}
                    >
                      {d === 1 ? "1 day" : `${d} days`}
                    </button>
                  ))}
                </div>
              </div>

              {/* ----------------- LINK PANE ----------------- */}
              {tab === "link" && (
                <div className="mt-6" data-testid="my-share-pane-link">
                  <label className="block text-[11.5px] font-bold tracking-[0.14em] uppercase text-trital-navy mb-2">
                    Your link
                  </label>
                  <div className="flex gap-2">
                    <input
                      readOnly
                      value={shareState.url}
                      onFocus={(e) => e.target.select()}
                      className="flex-1 px-3.5 py-2.5 rounded-xl border border-slate-300 bg-slate-50 text-[12.5px] text-slate-800 font-mono truncate focus:outline-none focus:ring-2 focus:ring-trital-navy/20 focus:border-trital-navy"
                      data-testid="my-share-url"
                      aria-label="Shareable URL"
                    />
                    <button
                      onClick={onCopy}
                      className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl bg-trital-navy text-white text-[12.5px] font-semibold btn-lift hover:bg-trital-navy-deep"
                      data-testid="my-share-copy"
                    >
                      <Copy className="w-3.5 h-3.5" strokeWidth={1.8} /> Copy
                    </button>
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-2 text-[11.5px] text-slate-500">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100">
                      <Clock className="w-3 h-3 text-trital-navy" strokeWidth={1.8} />
                      Expires {expiresDate?.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100">
                      <ShieldCheck className="w-3 h-3 text-trital-navy" strokeWidth={1.8} />
                      {sizeKB} KB · no server round-trip
                    </span>
                  </div>

                  {tooLarge && (
                    <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 flex items-start gap-2" data-testid="my-share-warning">
                      <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" strokeWidth={1.8} />
                      <div className="text-[12px] text-amber-900 leading-relaxed">
                        This link is larger than typical email-safe limits. For very detailed records, prefer the <strong>Download all (JSON)</strong> option and attach the file directly.
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ----------------- QR PANE ----------------- */}
              {tab === "qr" && (
                <div className="mt-6" data-testid="my-share-pane-qr">
                  <div className="text-[11.5px] font-bold tracking-[0.14em] uppercase text-trital-navy mb-3">
                    Scan-to-pull at a career fair
                  </div>

                  {qrFits ? (
                    <div className="flex flex-col items-center">
                      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-card" data-testid="my-share-qr-wrapper">
                        {qrDataUrl ? (
                          <img
                            src={qrDataUrl}
                            alt="Shareable checklist QR code"
                            className="w-[240px] h-[240px] block"
                            data-testid="my-share-qr-img"
                          />
                        ) : qrError ? (
                          <div className="w-[240px] h-[240px] flex items-center justify-center text-[12px] text-slate-500 text-center p-4">
                            QR generation failed: {qrError}
                          </div>
                        ) : (
                          <div className="w-[240px] h-[240px] flex items-center justify-center text-[12px] text-slate-400">
                            Generating…
                          </div>
                        )}
                      </div>
                      <p className="mt-3 text-[12.5px] text-slate-500 text-center max-w-xs">
                        Point a recruiter's phone camera at this code — it opens your read-only checklist instantly, no typing.
                      </p>
                      <div className="mt-4 flex flex-wrap gap-2 justify-center">
                        <button
                          onClick={handleDownloadQr}
                          disabled={!qrDataUrl}
                          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-trital-navy text-white text-[12.5px] font-semibold btn-lift hover:bg-trital-navy-deep disabled:opacity-50"
                          data-testid="my-share-qr-download"
                        >
                          <Download className="w-3.5 h-3.5" strokeWidth={1.8} /> Download PNG
                        </button>
                        <button
                          onClick={onCopy}
                          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-white border border-slate-300 text-[12.5px] font-semibold text-trital-navy hover:bg-slate-50"
                          data-testid="my-share-qr-copy-url"
                        >
                          <Copy className="w-3.5 h-3.5" strokeWidth={1.8} /> Copy link
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-2" data-testid="my-share-qr-too-large">
                      <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" strokeWidth={1.8} />
                      <div className="text-[12.5px] text-amber-900 leading-relaxed">
                        Your checklist is too detailed to fit inside a QR code ({sizeKB} KB · QR safe limit ≈ {(QR_FIT_MAX_BYTES / 1024).toFixed(1)} KB).
                        <span className="block mt-1">Switch to the <strong>Link</strong> tab and share the URL directly, or <strong>Download all (JSON)</strong> and attach the file.</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-7 flex flex-wrap gap-2">
                <button
                  onClick={onEmail}
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-trital-red text-white text-[13px] font-semibold btn-lift hover:bg-trital-red-deep"
                  data-testid="my-share-email"
                >
                  <Mail className="w-3.5 h-3.5" strokeWidth={1.8} /> Email it
                </button>
                <button
                  onClick={() => onRegenerate()}
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white border border-slate-300 text-[13px] font-semibold text-trital-navy hover:bg-slate-50"
                  data-testid="my-share-regenerate"
                >
                  <RefreshCcw className="w-3.5 h-3.5" strokeWidth={1.8} /> Regenerate
                </button>
                <Link
                  to="/c/scan"
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white border border-slate-300 text-[13px] font-semibold text-trital-navy hover:bg-slate-50"
                  data-testid="my-share-open-scanner"
                >
                  <ScanLine className="w-3.5 h-3.5" strokeWidth={1.8} /> Open scanner
                </Link>
                <button
                  onClick={onClose}
                  className="ml-auto inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white border border-slate-300 text-[13px] font-semibold text-slate-600 hover:bg-slate-50"
                  data-testid="my-share-done"
                >
                  Done
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

